import { useSyncExternalStore } from 'react';

/** How fast the trip is played: its seconds a second — a minute, ten minutes, an hour. */
export const SPEEDS = [60, 600, 3600] as const;
export type Speed = (typeof SPEEDS)[number];

/**
 * The moment the map shows the sky at, kept apart from the page: moved as the line is drawn
 * along or the trip plays, only what shows it is drawn again — the line and the map's sky, not
 * the plan around them.
 */
export interface Moment {
  at: () => number;
  /** Moved to a moment, kept within the trip. */
  set: (at: number) => void;
  /** The trip it is within, from its first instant to its last: now while it goes on, else
   * the nearest end, until it is moved. */
  within: (first: number, last: number) => void;
  playing: () => boolean;
  /** Played on from where it is; from the start again once it has reached the end. */
  play: () => void;
  pause: () => void;
  speed: () => Speed;
  setSpeed: (speed: Speed) => void;
  /** Told each change. */
  subscribe: (heard: () => void) => () => void;
}

export function createMoment(now: number = Date.now()): Moment {
  let [first, last] = [now, now];
  let at = now;
  let moved = false;
  let playing = false;
  let speed: Speed = 600;
  let frame = 0;
  const heard = new Set<() => void>();
  const tell = () => {
    for (const one of heard) one();
  };
  const inside = (to: number) => Math.min(last, Math.max(first, to));
  const step = (before: number) => (time: number) => {
    at = Math.min(last, at + (time - before) * speed);
    if (at >= last) playing = false;
    else frame = requestAnimationFrame(step(time));
    tell();
  };
  return {
    at: () => at,
    set: (to) => {
      moved = true;
      at = inside(to);
      tell();
    },
    within: (from, to) => {
      [first, last] = [from, to];
      at = inside(moved ? at : now);
      tell();
    },
    playing: () => playing,
    play: () => {
      if (at >= last) at = first;
      moved = true;
      playing = true;
      frame = requestAnimationFrame(step(performance.now()));
      tell();
    },
    pause: () => {
      cancelAnimationFrame(frame);
      playing = false;
      tell();
    },
    speed: () => speed,
    setSpeed: (to) => {
      speed = to;
      tell();
    },
    subscribe: (one) => {
      heard.add(one);
      return () => {
        heard.delete(one);
      };
    },
  };
}

/** What of a moment a part of the page shows, read again only when that changes. */
export function useMomentOf<T>(moment: Moment, read: (moment: Moment) => T): T {
  return useSyncExternalStore(moment.subscribe, () => read(moment));
}
